import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Badge,
  Row,
  Col,
  Collapse,
  Alert,
  Spinner
} from 'reactstrap';
import axios from 'axios';
import MappingEditor from './MappingEditor';

const MappingList = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMappingId, setExpandedMappingId] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseURL}/ssp-studio/mappings`, {
        withCredentials: true
      });
      if (response.data.success) {
        setMappings(response.data.data);
      } else {
        setError('Failed to load mappings');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (mappingId) => {
    try {
      const response = await axios.post(
        `${baseURL}/ssp-studio/mappings/${mappingId}/activate`,
        {},
        { withCredentials: true }
      );
      if (response.data.success) {
        fetchMappings();
      } else {
        setError('Failed to activate mapping');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this mapping set?')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${baseURL}/ssp-studio/mappings/${mappingId}`,
        { withCredentials: true }
      );
      if (response.data.success) {
        fetchMappings();
      } else {
        setError(response.data.message || 'Failed to delete mapping');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSeedDefault = async () => {
    try {
      setSeeding(true);
      const response = await axios.post(
        `${baseURL}/ssp-studio/mappings/seed/default`,
        {},
        { withCredentials: true }
      );
      if (response.data.success) {
        fetchMappings();
      } else {
        setError(response.data.message || 'Failed to seed default mapping');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSeeding(false);
    }
  };

  const toggleExpand = (mappingId) => {
    setExpandedMappingId(expandedMappingId === mappingId ? null : mappingId);
  };

  const handleEditMappings = (mapping) => {
    setEditingMapping(mapping);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingMapping(null);
  };

  const handleMappingSaved = () => {
    fetchMappings();
  };

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading mappings...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h4>Scan Field Mappings</h4>
          <p className="text-muted">
            Mapping sets define how scan file fields map to NIST controls and SSP sections
          </p>
        </Col>
        <Col xs="auto">
          <Button
            color="success"
            onClick={handleSeedDefault}
            disabled={seeding}
          >
            {seeding ? <Spinner size="sm" /> : <i className="mdi mdi-database-plus"></i>}
            {' '}Create Default FedRAMP Mapping
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mappings.length === 0 ? (
        <Card>
          <CardBody className="text-center">
            <i className="mdi mdi-table-search display-4 text-muted"></i>
            <h5 className="mt-3">No Mapping Sets Found</h5>
            <p className="text-muted">Create a default mapping to get started</p>
            <Button color="primary" onClick={handleSeedDefault} disabled={seeding}>
              {seeding ? <Spinner size="sm" /> : 'Create Default Mapping'}
            </Button>
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <Table hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Version</th>
                  <th>Framework</th>
                  <th>Mappings</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(mapping => (
                  <React.Fragment key={mapping._id}>
                    <tr>
                      <td>
                        <strong>{mapping.name}</strong>
                        {mapping.sourceFilename && (
                          <div className="text-muted small">
                            Imported from: {mapping.sourceFilename}
                          </div>
                        )}
                      </td>
                      <td>{mapping.version}</td>
                      <td>
                        {mapping.metadata?.framework || 'N/A'}
                        {mapping.metadata?.baseline && (
                          <div className="text-muted small">
                            {mapping.metadata.baseline}
                          </div>
                        )}
                      </td>
                      <td>{mapping.mappings?.length || 0} fields</td>
                      <td>
                        {mapping.isActive ? (
                          <Badge color="success">Active</Badge>
                        ) : (
                          <Badge color="secondary">Inactive</Badge>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          <Button
                            size="sm"
                            color="link"
                            onClick={() => toggleExpand(mapping._id)}
                          >
                            {expandedMappingId === mapping._id ? 'Hide' : 'View'}
                          </Button>
                          <Button
                            size="sm"
                            color="info"
                            outline
                            onClick={() => handleEditMappings(mapping)}
                          >
                            <i className="mdi mdi-pencil"></i> Edit
                          </Button>
                          {!mapping.isActive && (
                            <Button
                              size="sm"
                              color="primary"
                              onClick={() => handleActivate(mapping._id)}
                            >
                              Activate
                            </Button>
                          )}
                          {!mapping.isActive && (
                            <Button
                              size="sm"
                              color="danger"
                              onClick={() => handleDelete(mapping._id)}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="6" className="p-0">
                        <Collapse isOpen={expandedMappingId === mapping._id}>
                          <Card className="mb-0">
                            <CardHeader>
                              <strong>Field Mappings ({mapping.mappings?.length || 0})</strong>
                            </CardHeader>
                            <CardBody>
                              {mapping.mappings && mapping.mappings.length > 0 ? (
                                <Table size="sm" bordered>
                                  <thead>
                                    <tr>
                                      <th>Field Name</th>
                                      <th>Aliases</th>
                                      <th>Scan Group</th>
                                      <th>NIST Controls</th>
                                      <th>SSP Sections</th>
                                      <th>Severity Bias</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {mapping.mappings.map((field, idx) => (
                                      <tr key={idx}>
                                        <td><code>{field.fieldname}</code></td>
                                        <td>
                                          {field.aliases && field.aliases.length > 0 ? (
                                            field.aliases.map((alias, i) => (
                                              <Badge key={i} color="info" className="me-1">
                                                {alias}
                                              </Badge>
                                            ))
                                          ) : (
                                            <span className="text-muted">—</span>
                                          )}
                                        </td>
                                        <td>{field.scan_group || '—'}</td>
                                        <td>
                                          {field.nist_controls && field.nist_controls.length > 0 ? (
                                            field.nist_controls.map((ctrl, i) => (
                                              <Badge key={i} color="secondary" className="me-1">
                                                {ctrl}
                                              </Badge>
                                            ))
                                          ) : (
                                            <span className="text-muted">—</span>
                                          )}
                                        </td>
                                        <td>
                                          {field.ssp_sections && field.ssp_sections.length > 0 ? (
                                            field.ssp_sections.join(', ')
                                          ) : (
                                            <span className="text-muted">—</span>
                                          )}
                                        </td>
                                        <td>
                                          <Badge
                                            color={
                                              field.severity_bias === 'critical' ? 'danger' :
                                              field.severity_bias === 'high' ? 'warning' :
                                              field.severity_bias === 'medium' ? 'info' :
                                              'secondary'
                                            }
                                          >
                                            {field.severity_bias || 'medium'}
                                          </Badge>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              ) : (
                                <p className="text-muted">No mappings defined</p>
                              )}
                            </CardBody>
                          </Card>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* Mapping Editor Modal */}
      <MappingEditor
        mappingSet={editingMapping}
        isOpen={showEditor}
        toggle={handleEditorClose}
        onSaved={handleMappingSaved}
      />
    </div>
  );
};

export default MappingList;
